// Component: OrdersPage
// Purpose: Three-book tabbed view (Positions/Trades/Orders) with broker reconciliation, source-tag badges, risk flags, and live WS delta-patches.

import React, { useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TradingDataContext } from '../../contexts/TradingDataContext';
import OrdersTable from '../../components/trading/OrdersTable';
import PositionsTable from '../../components/trading/PositionsTable';
import HoldingsTable from '../../components/trading/HoldingsTable';
import TradeCard from '../../components/trading/TradeCard';
import OrderDetailDrawer from '../../components/trading/OrderDetailDrawer';
import ConfirmDialog from '../../components/trading/ConfirmDialog';
import ModifyOrderModal from '../../components/trading/ModifyOrderModal';
import { groupOrdersIntoTrades, getBrokerBadge } from '../../utils/tradingTransforms';
import { services } from '../../services/api';
import OrdersService from '../../api/services/orders';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { PageHeader, Badge, Button, Tabs, Select, Segmented, Icon } from '../../components/ds';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import MobileOrders from '../../components/mobile/MobileOrders';
import {
  BD_PAIRING_WINDOW_DAYS,
  pairingWindowDateRange,
  filterClosedTradesToDisplayRange,
  sortTradesForDisplay,
} from './tradesDisplay';

/*>>> CONSTANTS <<<*/

const TABS = ['Positions', 'Trades', 'Orders', 'Holdings'];

// Date range options → query param string
const DATE_RANGE_OPTIONS = [
  { label: '1D', value: '1D' },
  { label: '1W', value: '1W' },
  { label: '1M', value: '1M' },
];

//! Load-bearing honesty invariant (R102.5 AC(f)) — updated for consolidated view (C4 P4):
//! ALGO + broker-direct orders now surface. Reconcile adds broker-truth positions.
//! If Orders.test.jsx asserts verbatim on this string, update the test to match.
const LIVE_STREAM_TOOLTIP =
  'Live feed includes ALGO and broker-direct orders. For broker-truth positions and P&L, use Reconcile.';

// --> Convert preset date range label to from_dt / to_dt ISO strings (UTC midnight).
//   The consolidated endpoint expects from_dt / to_dt, not a preset string.
function _presetToDateRange(preset) {
  const now = new Date();
  const toISO = (d) => d.toISOString().split('T')[0]; // YYYY-MM-DD
  const today = toISO(now);
  if (preset === '1D') {
    return { from_dt: today, to_dt: today };
  }
  if (preset === '1W') {
    const d = new Date(now);
    d.setDate(d.getDate() - 7);
    return { from_dt: toISO(d), to_dt: today };
  }
  if (preset === '1M') {
    const d = new Date(now);
    d.setMonth(d.getMonth() - 1);
    return { from_dt: toISO(d), to_dt: today };
  }
  // Fallback: today only
  return { from_dt: today, to_dt: today };
}

/*>>> SUB-COMPONENTS <<<*/

// --> IST time formatter for reconcileAsOf display (HH:MM:SS)
const _istAsOfFmt = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const formatAsOf = (isoString) => {
  if (!isoString) return null;
  try {
    return _istAsOfFmt.format(new Date(isoString));
  } catch {
    return null;
  }
};

/*>>> TOP BAR <<<*/

/**
 * OrdersTopBar — date-range selector, account filter, reconcile controls,
 *   "reconciled as of" label, live-stream indicator with load-bearing tooltip.
 */
const OrdersTopBar = ({
  dateRange,
  onDateRangeChange,
  accountId,
  accounts,
  onAccountChange,
  brokerFilter,
  brokerOptions,
  onBrokerChange,
  reconcileAsOf,
  reconcileError,
  onReconcileNow,
  isConnected,
  //! C2 (T5) — in-flight + rate-limit feedback for the "Reconcile now" button
  reconcileInFlight,
  reconcileRateLimit,
}) => {
  const reconciledLabel = useMemo(() => {
    if (!reconcileAsOf) return 'Not yet reconciled';
    const hms = formatAsOf(reconcileAsOf);
    return hms ? `Reconciled as of ${hms}` : 'Not yet reconciled';
  }, [reconcileAsOf]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* date-range filter — DS Segmented (per-option testId passthrough keeps the
          date-range-* test hooks + aria-pressed) */}
      <Segmented
        aria-label="Date range"
        value={dateRange}
        onChange={onDateRangeChange}
        options={DATE_RANGE_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label, testId: `date-range-${opt.value}` }))}
      />

      {/* account filter — DS Select on the 32px rail */}
      <Select
        value={accountId || 'all'}
        onChange={(e) => onAccountChange(e.target.value === 'all' ? null : e.target.value)}
        aria-label="Account filter"
        data-testid="account-filter"
        className="text-xs"
        style={{ width: 'auto' }}
      >
        <option value="all">All accounts</option>
        {(accounts || []).map((acc) => (
          <option key={acc.account_id} value={acc.account_id}>
            {[acc.broker_display_name || acc.broker_name, acc.account_holder_name, acc.broker_user_id]
              .filter(Boolean).join(' · ') || acc.account_id}
          </option>
        ))}
      </Select>

      {/* R3.3 — broker filter (client-side over the loaded rows). Hidden when only one/zero brokers present. */}
      {(brokerOptions || []).length > 1 && (
        <Select
          value={brokerFilter || 'all'}
          onChange={(e) => onBrokerChange(e.target.value === 'all' ? null : e.target.value)}
          aria-label="Broker filter"
          data-testid="broker-filter"
          className="text-xs"
          style={{ width: 'auto' }}
        >
          <option value="all">All brokers</option>
          {(brokerOptions || []).map((b) => (
            <option key={b.value} value={b.value}>{b.label || getBrokerBadge(b.value)?.label || b.value}</option>
          ))}
        </Select>
      )}

      {/* divider */}
      <span className="h-5 w-px bg-[var(--divider)]" aria-hidden="true" />

      {/* R104.4 AC(c) — "Not yet reconciled" before first reconcile; never empty / zero-timestamp */}
      <span
        className={`text-xs tabular-nums ${reconcileError ? 'text-loss' : 'text-fg-2'}`}
        data-testid="reconcile-as-of-label"
      >
        {reconcileError ? 'Reconcile unavailable — try again' : reconciledLabel}
      </span>

      <Button
        variant="ghost"
        size="md"
        onClick={onReconcileNow}
        disabled={reconcileInFlight}
        data-testid="reconcile-now-btn"
      >
        <Icon name="refresh" size={16} className={reconcileInFlight ? 'animate-spin' : ''} />
        {reconcileInFlight ? 'Reconciling…' : 'Reconcile now'}
      </Button>

      {/* C2 (T5) — rate-limited feedback: never silent-success on a 429'd sweep */}
      {reconcileRateLimit && (
        <span className="text-xs text-warning" data-testid="reconcile-rate-limit-label">
          Rate-limited — retry in {reconcileRateLimit.retryAfter}s
        </span>
      )}

      {/* divider */}
      <span className="h-5 w-px bg-[var(--divider)]" aria-hidden="true" />

      {/*>>> LIVE STREAM INDICATOR <<<*/}
      {/* R102.5 AC(f) — tooltip text is load-bearing; test asserts verbatim */}
      <Badge
        variant={isConnected ? 'profit' : 'neutral'}
        size="sm"
        dot
        pulse={isConnected}
        title={LIVE_STREAM_TOOLTIP}
        aria-label={LIVE_STREAM_TOOLTIP}
        data-testid="live-stream-indicator"
      >
        {isConnected ? 'Live' : 'Offline'}
      </Badge>
    </div>
  );
};

// --> Per-broker degraded banner (R106.7) — amber, distinct from "No orders yet" empty state
const BrokerDegradedBanner = ({ broker, error, errorCode }) => {
  //* NEW-I5 — branch on structured error_code enum, never on error.message string-matching
  let message;
  if (errorCode === 'NO_ACTIVE_SESSION') {
    message = `Broker session expired. Please log in to ${(broker || 'your broker').toUpperCase()} to resume live data.`;
  } else if (errorCode === 'ADAPTER_ERROR') {
    message = `Unable to fetch orders from ${(broker || 'broker').toUpperCase()} — adapter error. Retrying automatically.`;
  } else {
    message = `${(broker || 'Broker').toUpperCase()} unavailable — check broker connection`;
  }
  return (
    <div
      data-testid={`broker-degraded-banner-${(broker || 'unknown').toLowerCase()}`}
      className="mb-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning"
      role="alert"
    >
      <span aria-hidden="true">&#9888;</span>
      <div>
        <span className="font-semibold">{message}</span>
        {error && !errorCode ? <div className="mt-0.5 text-xs text-warning/80">{error}</div> : null}
      </div>
    </div>
  );
};

// --> R104.14 — three independent structured degraded banners (driven by flags, NOT string-matching)

//! oms_degraded: DeeTrade OMS unreachable → showing broker data only
const OmsDegradedBanner = () => (
  <div
    data-testid="oms-degraded-banner"
    className="mb-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning"
    role="alert"
  >
    <span aria-hidden="true">&#9888;</span>
    <span>
      <span className="font-semibold">DeeTrade orders unavailable</span>
      {' '}— showing broker data only. Algo order history may be incomplete.
    </span>
  </div>
);

//! D7/M1 — 4th INDEPENDENT banner: OMS positions leg down. Own text, NEVER conflated with the orders
//  banner (a positions-only outage must not falsely claim orders are unavailable).
const OmsPositionsDegradedBanner = () => (
  <div
    data-testid="oms-positions-degraded-banner"
    className="mb-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning"
    role="alert"
  >
    <span aria-hidden="true">&#9888;</span>
    <span>
      <span className="font-semibold">DeeTrade positions unavailable</span>
      {' '}— showing broker data only. Algo/manual position tracking may be incomplete.
    </span>
  </div>
);

//! classification_degraded: attribution service unreachable → provenance unverifiable
const ClassificationDegradedBanner = () => (
  <div
    data-testid="classification-degraded-banner"
    className="mb-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning"
    role="alert"
  >
    <span aria-hidden="true">&#9888;</span>
    <span>
      <span className="font-semibold">Classification unavailable</span>
      {' '}— provenance (algo/manual/external) cannot be verified for this session.
    </span>
  </div>
);

//! broker_degraded: broker snapshot stale → show broker_orders_as_of timestamp in IST
const BrokerDataStaleBanner = ({ asOf }) => {
  const label = asOf ? formatAsOf(asOf) : null;
  //! Only show when broker_orders_as_of is present (stale day detection)
  if (!label) return null;
  return (
    <div
      data-testid="broker-data-stale-banner"
      className="mb-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning"
      role="alert"
    >
      <span aria-hidden="true">&#9888;</span>
      <span>
        <span className="font-semibold">Broker data may be stale</span>
        {' '}— last updated {label} IST. Reconcile now for fresh data.
      </span>
    </div>
  );
};

/*>>> MAIN COMPONENT <<<*/

/**
 * OrdersPage — reads TradingDataContext (provided by TradingDataLayout higher up the
 * router tree). Does NOT mount its own TradingDataProvider — the layout wrapper
 * provides it so tab switching cannot unmount/remount it (R102.1 AC(b)).
 */
function OrdersPage() {
  //* Context access — TradingDataProvider is the ancestor via TradingDataLayout
  const ctx = useContext(TradingDataContext);
  if (!ctx) {
    // Safety: if somehow rendered outside layout, surface a clear error
    return (
      <div className="p-8 text-loss text-sm">
        OrdersPage must be rendered within TradingDataLayout / TradingDataProvider.
      </div>
    );
  }
  //! All hooks live in OrdersPageInner so they run unconditionally (react-hooks/rules-of-hooks):
  //  the !ctx guard above is the only early return and it precedes every hook except useContext.
  return <OrdersPageInner ctx={ctx} />;
}

/**
 * OrdersPageInner — owns all state/effects/derived data + render. Only mounted once ctx is
 * present (the guard lives in OrdersPage), so every hook below runs on every render of this
 * component — no early-return-before-hooks.
 */
function OrdersPageInner({ ctx }) {
  const {
    ordersRef,
    positionsRef,
    deploymentsRef,
    versions,
    isConnected,
    runReconcile,
    reconcileAsOf,
    reconcileError,
    reconcileSnapshotRef,
    brokerStatus,
    //! R104.14 — three independent structured flags (never string-matched)
    omsDegraded,
    omsPositionsDegraded,
    classificationDegraded,
    brokerDegraded,
    brokerOrdersAsOf,
  } = ctx;

  //* R5.1/R6.4 — surface cancel/modify results to the operator via the existing app-wide
  //  snackbar (mounted at App.js:68, no-op default context if ever rendered outside it —
  //  reused rather than inventing a new toast mechanism, per feedback_frontend_file_headers).
  const { showSnackbar } = useSnackbar();
  const { isPhone } = useBreakpoint();

  /*>>> STATE <<<*/

  const [activeTab, setActiveTab] = useState('Positions');
  //! DEFAULT '1W' — '1D' (today) is almost always empty during development and early live.
  //! Switching to 1W shows the last 7 days so operators immediately see recent orders.
  const [dateRange, setDateRange] = useState('1W');
  const [accountId, setAccountId] = useState(null);
  const [accounts, setAccounts] = useState([]);

  //! 10494-13 change 26 — Holdings tab state. Fetched LAZILY on first activation of the
  //  Holdings tab (never on page load) to keep the broker REST quota off every Orders-page
  //  load; holdingsFetchedRef guards against re-firing on every subsequent tab switch.
  const [holdings, setHoldings] = useState([]);
  const [holdingsAccounts, setHoldingsAccounts] = useState([]);
  const [holdingsAsOf, setHoldingsAsOf] = useState(null);
  const [holdingsLoading, setHoldingsLoading] = useState(false);
  const [holdingsError, setHoldingsError] = useState(null);
  const holdingsFetchedRef = useRef(false);

  //! 104 D9 — user-scoped rejection surface (algo + manual together). Fetched via
  //  getRejectedSignals({}) — NO strategy_instance_id — so the gateway/OMS returns every
  //  rejection tenant-scoped to the caller's own siis. rejectionsDegraded mirrors the
  //  response's oms_degraded flag; a failed/degraded fetch must never break the Orders view.
  const [rejections, setRejections] = useState([]);
  const [rejectionsDegraded, setRejectionsDegraded] = useState(false);
  //! R3.3 — client-side broker filter (null = all). Options derive from the loaded row set; no API param.
  const [brokerFilter, setBrokerFilter] = useState(null);
  const [drawerOrder, setDrawerOrder] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  //! #10474 — lifecycle-timeline events for the open drawer (null = not fetched/reset).
  const [timelineEvents, setTimelineEvents] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  //  Fetch failed: distinct from "fetched, genuinely empty" ([]) and "not fetched yet" (null).
  const [timelineError, setTimelineError] = useState(false);
  //  Order id the drawer currently shows; a late response for any other id is discarded.
  const timelineOidRef = useRef(null);

  //! R5.2.1 — pending cancel confirm: { orderId, resolve } while the confirm dialog is open.
  //  `resolve` settles the Promise OrdersTable's CancelButton awaits (its disabled-while-pending
  //  guard, R5.2) regardless of whether the operator confirms or dismisses.
  const [cancelConfirm, setCancelConfirm] = useState(null);

  //! R6.1 — order currently open in the Modify modal (null = closed)
  const [modifyOrder, setModifyOrder] = useState(null);

  //! C2 (T5) — "Reconcile now" in-flight + rate-limit feedback state.
  //  reconcileInFlight: disables the button + swaps its label while runReconcile is pending.
  //  reconcileRateLimit: { retryAfter } when the last reconcile's POST sweep was 429'd —
  //  cleared on the next non-rate-limited result.
  //! reconcileInFlightRef mirrors the state but is read/written SYNCHRONOUSLY — several
  //! fireEvent.click calls fired within one React batch would otherwise all read the
  //! same stale (pre-update) `reconcileInFlight` state closure and slip past the guard.
  const [reconcileInFlight, setReconcileInFlight] = useState(false);
  const [reconcileRateLimit, setReconcileRateLimit] = useState(null);
  const reconcileInFlightRef = useRef(false);
  //! F2 — single interval ref for the rate-limit countdown; a NEW 429 clears any running
  //  interval before starting a fresh one, and it is cleared on unmount.
  const reconcileRateLimitIntervalRef = useRef(null);

  //! windowedOrdersVersion — bumped whenever windowedOrdersRef is replaced (REPLACE, not accumulate).
  //! This is the SOLE trigger for the orders memo re-computation on this page.
  //! The shared context versions.orders / versions.reconcile still drive WS patches
  //! into windowedOrdersRef via the sync effect below.
  const [windowedOrdersVersion, setWindowedOrdersVersion] = useState(0);

  /*>>> WINDOWED ORDERS REF <<<*/

  //! windowedOrdersRef — page-owned Map, keyed by order_id, scoped to the selected window.
  //! REPLACED (not accumulated) on every windowed GET (initial load, window change, account change,
  //! reconcile). WS delta patches are applied on top of this base set without replacing it.
  //!
  //! WHY this is separate from ordersRef:
  //!   ordersRef is a shared, accumulating Map seeded by TradingDataContext.initialize() with
  //!   the latest-100 orders (no date window) plus WS updates from the algo-dashboard. It is
  //!   NOT scoped to any user-selected date range.
  //!   Reading ordersRef directly on the Orders page means the visible set is "whatever was
  //!   in the accumulating Map", not "the orders in the selected window" — the root cause of
  //!   the ~10-row cap bug. windowedOrdersRef fixes this by holding exactly the windowed set.
  const windowedOrdersRef = useRef(new Map());

  /*>>> WIDE PAIRING-INPUT REF (honest-B) <<<*/

  //! pairingOrdersRef — SEPARATE page-owned Map (BD_PAIRING_WINDOW_DAYS = 120d), keyed by
  //! order_id. This is ONLY the FIFO-pairing input for the Trades tab — the Orders TABLE
  //! keeps reading the display-windowed `orders` above (arch-critic rev1#6 — do not widen
  //! the shared display window). A trade whose opening leg falls outside the user's
  //! selected display range (1D/1W/1M) still pairs correctly against this wider real set;
  //! the resulting CLOSED groups are then display-filtered back down by exit date
  //! (filterClosedTradesToDisplayRange, honest-B step 3) before rendering.
  //! Fetched via the SAME local GET as windowedOrdersRef (services.trading.getLiveOrders →
  //! /orders/consolidated) — reads already-swept broker_orders rows, NOT a broker sweep.
  const pairingOrdersRef = useRef(new Map());
  const [pairingVersion, setPairingVersion] = useState(0);

  /*>>> DERIVED DATA <<<*/

  // --> Memoize orders from the WINDOWED ref (page-local, replaced on each fetch).
  //! LIVE/BROKER-ONLY surface: paper orders excluded at this consumption point.
  //! Re-computed only when windowedOrdersVersion bumps (set after each windowed REPLACE).
  //! #109 Live-Val 2026-09-08 — strategy NAME, not the composite strategy_instance_id
  //!  ("user:strategy_id:version_id:instrument_id:tf"), which rendered as two UUIDs in the
  //!  Strategy column. Deployments (already in context, keyed by deployment_id) carry
  //!  strategy_id/version_id/strategy_name; index them by the sii's strategy_id:version_id pair.
  const strategyNameBySiiPair = useMemo(() => {
    const map = {};
    const deps = deploymentsRef?.current ? Array.from(deploymentsRef.current.values()) : [];
    deps.forEach((dep) => {
      const name = dep?.strategy_name || dep?.name;
      if (!name) return;
      if (dep.strategy_id && dep.version_id) map[`${dep.strategy_id}:${dep.version_id}`] = name;
      if (dep.strategy_id) map[dep.strategy_id] = map[dep.strategy_id] || name;
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions.deployments]);

  const withStrategyName = useCallback((row) => {
    if (!row || row.strategy_name || !row.strategy_instance_id) return row;
    const parts = String(row.strategy_instance_id).split(':');
    const name = strategyNameBySiiPair[`${parts[1]}:${parts[2]}`] || strategyNameBySiiPair[parts[1]];
    return name ? { ...row, strategy_name: name } : row;
  }, [strategyNameBySiiPair]);

  //! #109 Live-Val 2026-09-08 — broker attribution for every row. OMS-origin rows carry
  //!  account_id but broker=null (the view's OMS leg has no broker column), so the broker
  //!  filter could never match them and "All accounts" never told the user which broker a row
  //!  belongs to. Derive broker + display name from the accounts list (the same source the
  //!  account dropdown uses) whenever the row lacks it.
  const brokerByAccount = useMemo(() => {
    const map = {};
    (accounts || []).forEach((acc) => {
      if (acc.account_id) {
        map[acc.account_id] = {
          broker: acc.broker_name || null,
          broker_display_name: acc.broker_display_name || acc.broker_name || null,
          broker_client_id: acc.broker_user_id || acc.broker_client_id || null,
        };
      }
    });
    return map;
  }, [accounts]);

  const withBroker = useCallback((row) => {
    if (!row) return row;
    const acc = row.account_id ? brokerByAccount[row.account_id] : null;
    const broker = row.broker || acc?.broker || null;
    const display = row.broker_display_name || acc?.broker_display_name || broker;
    if (broker === row.broker && display === row.broker_display_name) return row;
    return {
      ...row,
      broker,
      broker_display_name: display,
      broker_client_id: row.broker_client_id || acc?.broker_client_id || null,
    };
  }, [brokerByAccount]);

  //! #109 Live-Val 2026-09-08 — the account filter is applied client-side on EVERY tab.
  //!  The fetches already pass account_id to the backend, but rows loaded before the
  //!  selection (and WS-merged rows) stayed in the refs, so changing the dropdown never
  //!  changed the table. Rows with no account_id (OMS paper/legacy) stay visible only
  //!  under "All accounts".
  const matchesAccount = useCallback(
    (row) => !accountId || String(row?.account_id || '') === String(accountId),
    [accountId]
  );
  //! Broker filter — R3.3 extended from the Orders tab to every tab (Positions, Trades too);
  //!  compared on the canonical broker slug after withBroker() so OMS rows match as well.
  const matchesBroker = useCallback(
    (row) => !brokerFilter || String(row?.broker || '').toLowerCase() === String(brokerFilter).toLowerCase(),
    [brokerFilter]
  );

  //--> account-scoped, broker-enriched, NOT yet broker-filtered — the broker dropdown's option
  //    set must come from here, or selecting one broker would remove the others from the list.
  const ordersAllBrokers = useMemo(
    () =>
      Array.from(windowedOrdersRef.current.values()).filter(
        (o) => (o.execution_mode || '').toLowerCase() !== 'paper' && matchesAccount(o)
      ).map(withBroker).map(withStrategyName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [windowedOrdersVersion, matchesAccount, withBroker, withStrategyName]
  );
  const orders = useMemo(() => ordersAllBrokers.filter(matchesBroker), [ordersAllBrokers, matchesBroker]);

  // --> R3.3 — distinct broker values present in the current row set (for the filter dropdown).
  //! Options come from the ACCOUNTS list (stable, independent of the current broker filter and
  //!  of which tab has rows), labelled with the broker display name.
  const brokerOptions = useMemo(() => {
    const seen = new Map();
    const labelFor = (slug, display) => getBrokerBadge(slug)?.label || display || slug;
    (accounts || []).forEach((acc) => {
      const slug = acc.broker_name;
      if (slug && !seen.has(slug)) seen.set(slug, labelFor(slug, acc.broker_display_name));
    });
    ordersAllBrokers.forEach((o) => { if (o.broker && !seen.has(o.broker)) seen.set(o.broker, labelFor(o.broker, o.broker_display_name)); });
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [accounts, ordersAllBrokers]);

  // --> R3.3 — client-side broker filter applied to the displayed orders (case-insensitive).
  const displayedOrders = orders; // broker filter now applied inside `orders` (every tab)

  // --> honest-B: memoize orders from the WIDE pairing ref (separate from `orders` above).
  const pairingOrders = useMemo(
    () =>
      Array.from(pairingOrdersRef.current.values()).filter(
        (o) => (o.execution_mode || '').toLowerCase() !== 'paper' && matchesAccount(o)
      ).map(withBroker).map(withStrategyName).filter(matchesBroker),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pairingVersion, matchesAccount, matchesBroker, withBroker, withStrategyName]
  );

  //! 104 D9 — account_id (UUID) → human account label, reusing the SAME accounts list already
  //  fetched for the account-filter dropdown (no second accounts fetch).
  const accountLabelMap = useMemo(() => {
    const map = {};
    (accounts || []).forEach((acc) => {
      if (acc.account_id) map[acc.account_id] = acc.account_holder_name || acc.account_id;
    });
    return map;
  }, [accounts]);

  // --> 104 D9 — rejections scoped to the current account filter (client-side; the endpoint
  //   itself is always called user-scoped with no account param) + account label attached.
  const displayedRejections = useMemo(() => {
    const scoped = accountId
      ? (rejections || []).filter((r) => r.account_id === accountId)
      : (rejections || []);
    return scoped.map((r) => ({
      ...r,
      account_label: r.account_id ? (accountLabelMap[r.account_id] || r.account_id) : null,
    }));
  }, [rejections, accountId, accountLabelMap]);

  // --> Memoize positions array from positionsRef (account-filtered, strategy-named)
  const positions = useMemo(
    () => Array.from(positionsRef.current.values()).filter(matchesAccount).map(withBroker).map(withStrategyName).filter(matchesBroker),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [versions.positions, matchesAccount, matchesBroker, withBroker, withStrategyName]
  );

  // --> Group orders into trades for the Trades tab.
  //   A — FIFO-pair over the WIDE pairing set (honest-B), not the display-windowed `orders`.
  //   honest-B step 3 — display-filter the resulting CLOSED groups back to the user's
  //   selected range by exit date (IST calendar day); OPEN groups always shown.
  //   A (sort) — Trades-tab consumer sort: closed round-trip pairs first (sortTradesForDisplay.js),
  //   NOT groupOrdersIntoTrades' own open-first §5 sort (that stays shared/unchanged).
  const trades = useMemo(() => {
    const grouped = groupOrdersIntoTrades(pairingOrders, positions) || [];
    const displayFiltered = filterClosedTradesToDisplayRange(grouped, dateRange);
    return sortTradesForDisplay(displayFiltered);
  }, [pairingOrders, positions, dateRange]);

  /*>>> EFFECTS <<<*/

  // --> On mount: fetch account list for the filter dropdown (fire-and-forget)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await services.brokerAccounts.getAccounts();
        if (cancelled) return;
        //! #109 Live-Val 2026-09-08: BIS GET /broker-accounts/ returns a PLAIN LIST (response_model
        //!  List[BrokerAccountResponse]); the gateway wraps it as {status, data:[...]} — never
        //!  {accounts:[...]}. Reading `.accounts` left this dropdown permanently empty. Same
        //!  parse as WatchList.jsx / ExchangeSettingsPanel.jsx.
        const payload = res?.data;
        const accs = Array.isArray(payload) ? payload
          : Array.isArray(payload?.data) ? payload.data
          : (payload?.data?.accounts || payload?.accounts || []);
        setAccounts(accs);
      } catch (err) {
        console.log('[DIAG] OrdersPage — getAccounts failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // --> 104 D9 — user-scoped rejection surface fetch. Fires on mount AND whenever accountId
  //   changes (mirrors the other windowed/pairing fetches' trigger points), even though the
  //   call itself is always user-scoped ({} — no strategy_instance_id, no account param) so
  //   ALL of the caller's rejections come back; the account filter is then applied client-side
  //   (displayedRejections memo above). Fire-and-forget: a failure here must never break the
  //   Orders view — caught and logged, prior rows (if any) are left in place.
  useEffect(() => {
    let cancelled = false;
    console.log('[DIAG] OrdersPage — rejections fetch', { account_id: accountId });
    (async () => {
      try {
        const res = await services.trading.getRejectedSignals({});
        if (cancelled) return;
        const data = res?.data?.data ?? res?.data ?? {};
        const rows = data.rejected_signals || [];
        setRejections(rows);
        setRejectionsDegraded(Boolean(data.oms_degraded));
        console.log('[DIAG] OrdersPage — rejections fetch done', {
          count: rows.length, oms_degraded: Boolean(data.oms_degraded),
        });
      } catch (err) {
        console.log('[DIAG] OrdersPage — rejections fetch failed', err);
        // Intentionally no state change — leave any prior rejections in place, orders view unaffected.
      }
    })();
    return () => { cancelled = true; };
  }, [accountId]);

  // --> Initial windowed fetch (fires once on mount, Layer-1 per R104.9).
  //   Uses the INITIAL dateRange ('1W') so the user immediately sees 7 days of orders.
  //   REPLACES windowedOrdersRef (not accumulate) so the visible set equals the window.
  //   Also seeds positionsRef from the response's positions array.
  //
  //! FIX: previously used dateRange='1D' (today = 0 orders) and only merged via
  //!   `if (!ordersRef.current.has(key))` into the shared accumulating ordersRef —
  //!   the shared ref was already seeded with ~100 unwindowed rows from initialize(),
  //!   so new windowed rows were silently dropped and stale rows persisted.
  useEffect(() => {
    let cancelled = false;
    const dateParams = _presetToDateRange('1W'); // Always fetch 1W on initial mount
    console.log('[DIAG] OrdersPage — initial windowed fetch', { ...dateParams, account_id: accountId });
    (async () => {
      try {
        const res = await services.trading.getLiveOrders({ ...dateParams, account_id: accountId });
        if (cancelled) return;

        // --> REPLACE windowedOrdersRef with the windowed response (not accumulate).
        //   This is the key fix: every windowed fetch starts with a fresh Map so
        //   stale rows from the shared ordersRef never bleed into the visible set.
        const rows = res?.data?.data?.orders || res?.data?.orders || [];
        const nextMap = new Map();
        rows.forEach((order) => {
          const key = order.order_id || order.id;
          if (!key) return;
          const epochMs = order.created_at ? new Date(order.created_at).getTime() : Date.now();
          nextMap.set(key, { ...order, _epoch: epochMs });
        });
        windowedOrdersRef.current = nextMap;

        // C4 P4: seed positionsRef from the consolidated response (includes BD positions)
        //! Upsert through the registry — it derives the full-identity key (BD rows get the
        //! broker+instrument composite the old _positionKey produced) and merges idempotently.
        const posRows = res?.data?.data?.positions || res?.data?.positions || [];
        posRows.forEach((pos) => {
          positionsRef.current.upsert(pos);
        });
        //! #109 Live-Val 2026-09-08 — broker-held positions the platform does NOT own (e.g. an
        //!  ICICI holding bought outside DeeTrade) arrive as `broker_position_observations`
        //!  (consolidated_view.py: #118 Q2 never promotes a broker snapshot to an OMS POSITION).
        //!  Render them as BD (broker-truth) rows so the Positions tab shows what the broker
        //!  shows, keyed leg:broker:symbol:account by the registry — never merged into an OMS
        //!  row. An observation that matches an OMS position is already attached to that row
        //!  as broker_observed_net_qty and is skipped here.
        const observed = res?.data?.data?.broker_position_observations
          || res?.data?.broker_position_observations || [];
        //--> A broker symbol may carry the exchange series suffix ("YESBANK-EQ") while the OMS
        //    row carries the bare display symbol ("YESBANK"); compare on the bare form so the
        //    broker snapshot of a platform-owned position is attached, not shown as a 3rd row.
        const bareSym = (v) => String(v || '').toUpperCase().replace(/-(EQ|BE|BZ|SM|ST|BL)$/, '');
        //    The OMS row may carry no exchange at all, so the exchange is only compared when
        //    both sides have one.
        const omsKeys = new Set(posRows.map((p) => `${p.account_id || ''}|${bareSym(p.instrument_symbol || p.symbol)}`));
        observed.forEach((obs) => {
          const qty = Number(obs.broker_observed_net_qty);
          if (!Number.isFinite(qty) || qty === 0) return;
          if (omsKeys.has(`${obs.account_id || ''}|${bareSym(obs.symbol)}`)) return;
          positionsRef.current.upsert({
            leg: 'BD',
            source_tag: 'BD',
            broker: obs.broker,
            broker_client_id: obs.broker_client_id,
            account_id: obs.account_id,
            instrument_id: null,
            symbol: obs.symbol,
            instrument_symbol: obs.symbol,
            exchange: obs.exchange,
            product_type: obs.product_type,
            side: qty > 0 ? 'LONG' : 'SHORT',
            qty: String(Math.abs(qty)),
            quantity: String(Math.abs(qty)),
            avg_entry_price: obs.broker_observed_avg_price,
            entry_price: obs.broker_observed_avg_price,
            broker_observed_net_qty: String(qty),
            broker_as_of: obs.observed_at,
            execution_mode: 'live',
          });
        });

        // Bump windowed version so the orders memo re-computes
        setWindowedOrdersVersion((v) => v + 1);
        console.log('[DIAG] OrdersPage — initial windowed fetch done', { orders: rows.length, positions: posRows.length });
      } catch (err) {
        console.log('[DIAG] OrdersPage — initial windowed fetch failed', err);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty — fires exactly once on mount

  // --> honest-B step 1 — wide REAL pairing-input fetch (BD_PAIRING_WINDOW_DAYS = 120d).
  //   Fires on mount AND whenever accountId changes (mirrors the initial windowed fetch
  //   above, but scoped to accountId too — the pairing walk must be account-scoped the
  //   same way the display window is). REPLACES pairingOrdersRef (not accumulate).
  //!  This is a LOCAL GET over already-swept broker_orders rows (same endpoint as the
  //!  windowed fetch above) — NOT a broker sweep/refreshConsolidated call. It must never
  //!  call runReconcile/refreshConsolidated itself (that stays on its own cadence).
  useEffect(() => {
    let cancelled = false;
    const dateParams = pairingWindowDateRange();
    console.log('[DIAG] OrdersPage — wide pairing fetch (honest-B)', {
      ...dateParams, account_id: accountId, windowDays: BD_PAIRING_WINDOW_DAYS,
    });
    (async () => {
      try {
        const res = await services.trading.getLiveOrders({ ...dateParams, account_id: accountId });
        if (cancelled) return;

        const rows = res?.data?.data?.orders || res?.data?.orders || [];
        const nextMap = new Map();
        rows.forEach((order) => {
          const key = order.order_id || order.id;
          if (!key) return;
          const epochMs = order.created_at ? new Date(order.created_at).getTime() : Date.now();
          nextMap.set(key, { ...order, _epoch: epochMs });
        });
        pairingOrdersRef.current = nextMap;

        setPairingVersion((v) => v + 1);
        console.log('[DIAG] OrdersPage — wide pairing fetch done', { orders: rows.length });
      } catch (err) {
        console.log('[DIAG] OrdersPage — wide pairing fetch failed', err);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]); // mount + accountId change

  // --> Non-blocking reconcile after initial render (Layer-3 per R104.9).
  //   Now passes the current window (1W) so the POST sweep backfills 7 days,
  //   and the GET returns the windowed consolidated set.
  //!  FIX: previously called runReconcile({ force: false, account_id }) with NO
  //!  from_dt/to_dt — the POST swept today-only, and the GET returned today-only,
  //!  but runReconcile only PATCHES existing rows in ordersRef (does not replace).
  //!  Now the reconcile window matches the selected dateRange so the POST sweep
  //!  backfills the full window and the GET enriches the windowed rows with
  //!  reconcile_state + broker fields.
  useEffect(() => {
    const dateParams = _presetToDateRange('1W');
    const id = setTimeout(() => {
      console.log('[DIAG] OrdersPage — firing non-blocking initial runReconcile', { ...dateParams, accountId });
      runReconcile({ force: false, ...dateParams, account_id: accountId });
    }, 0);
    return () => clearTimeout(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty — fires exactly once on mount

  // --> 10494-13 change 26 — LAZY Holdings fetch, fired on FIRST activation of the
  //   Holdings tab only (never on page load — that is what keeps the broker REST quota
  //   off every Orders-page load, N/A on any other tab switch thereafter).
  useEffect(() => {
    if (activeTab !== 'Holdings' || holdingsFetchedRef.current) return undefined;
    holdingsFetchedRef.current = true;
    let cancelled = false;
    setHoldingsLoading(true);
    setHoldingsError(null);
    //! No account_id param is sent: GET /api/v1/holdings is user-scoped by the gateway
    //  X-User-ID header and BIS fans out over ALL of the caller's accounts. Passing
    //  account_id here would be a param the backend silently ignores — i.e. a filter the
    //  tab looks like it has and does not.
    console.log('[DIAG] OrdersPage — Holdings tab first activation, fetching');
    (async () => {
      try {
        const res = await services.trading.getHoldings();
        if (cancelled) return;
        const data = res?.data?.data || res?.data || {};
        setHoldings(data.holdings || []);
        setHoldingsAccounts(data.accounts || []);
        setHoldingsAsOf(data.as_of || null);
        console.log('[DIAG] OrdersPage — Holdings fetch done', {
          holdings: (data.holdings || []).length,
          accounts: (data.accounts || []).length,
        });
      } catch (err) {
        if (cancelled) return;
        console.log('[DIAG] OrdersPage — Holdings fetch failed', err);
        setHoldingsError(err?.message || 'Failed to load holdings');
        //! A failed top-level fetch must not leave a silently-empty table — surface the
        //  error rather than falling through to an empty accounts[] render.
      } finally {
        if (!cancelled) setHoldingsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // --> WS delta sync: when the shared context ordersRef receives a WS update
  //   (versions.orders bumps), apply the delta onto windowedOrdersRef IF the
  //   order_id is already in the windowed set (do not import orders outside
  //   the window from WS). Also apply reconcile_state patches from versions.reconcile.
  //
  //! This preserves the R102.5 / R104.13 merge semantics: WS deltas are additive,
  //! terminal-status monotonic, and never clobber reconcile_state.
  //! New WS orders that arrive from a live strategy while the page is open
  //! ARE imported (unconditionally added) so live fills appear in real-time.
  useEffect(() => {
    // Sync all entries from the shared ordersRef into windowedOrdersRef:
    //   - Orders already in the windowed set → update (WS delta/reconcile patch)
    //   - New live orders not yet in the windowed set → add them (live fill event)
    //   - Filter: paper orders excluded (same as the orders memo below)
    let changed = false;
    let pairingChanged = false;
    ordersRef.current.forEach((order, key) => {
      if ((order.execution_mode || '').toLowerCase() === 'paper') return;
      const existing = windowedOrdersRef.current.get(key);
      if (existing) {
        // Patch: merge WS delta onto the windowed row (same semantics as context handler)
        const merged = { ...existing, ...order };
        // Preserve reconcile_state if the incoming delta doesn't carry it
        if (!Object.prototype.hasOwnProperty.call(order, 'reconcile_state') && existing.reconcile_state != null) {
          merged.reconcile_state = existing.reconcile_state;
        }
        windowedOrdersRef.current.set(key, merged);
        changed = true;
      } else if (order._epoch && order._epoch > Date.now() - 60 * 60 * 1000) {
        // Add new orders that arrived via WS in the last hour (live fills during the session).
        // The 1-hour recency guard prevents importing stale unrelated orders that happen to
        // arrive in ordersRef from initialize() after the windowed fetch returns.
        windowedOrdersRef.current.set(key, order);
        changed = true;
      }

      // --> honest-B: ALSO sync the same live delta into pairingOrdersRef (the WIDE
      //   FIFO-pairing input) — same merge/import semantics as windowedOrdersRef above —
      //   so a live fill that closes a wide-window trade updates the pairing walk in
      //   real time, not just the next full pairing re-fetch.
      const existingPairing = pairingOrdersRef.current.get(key);
      if (existingPairing) {
        const mergedPairing = { ...existingPairing, ...order };
        if (!Object.prototype.hasOwnProperty.call(order, 'reconcile_state') && existingPairing.reconcile_state != null) {
          mergedPairing.reconcile_state = existingPairing.reconcile_state;
        }
        pairingOrdersRef.current.set(key, mergedPairing);
        pairingChanged = true;
      } else if (order._epoch && order._epoch > Date.now() - 60 * 60 * 1000) {
        pairingOrdersRef.current.set(key, order);
        pairingChanged = true;
      }
    });
    if (changed) {
      setWindowedOrdersVersion((v) => v + 1);
    }
    if (pairingChanged) {
      setPairingVersion((v) => v + 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions.orders, versions.reconcile]);

  /*>>> HANDLERS <<<*/

  // --> Fetch a new windowed set, REPLACE windowedOrdersRef, bump version.
  //   Shared by date-range change and account-filter change.
  //   Also calls runReconcile to POST-sweep + GET-enrich the windowed set.
  const _fetchAndReplaceWindow = useCallback(async (range, acctId) => {
    const dateParams = _presetToDateRange(range);
    console.log('[DIAG] OrdersPage — fetching windowed set', { ...dateParams, account_id: acctId });
    try {
      const res = await services.trading.getLiveOrders({ ...dateParams, account_id: acctId });
      const rows = res?.data?.data?.orders || res?.data?.orders || [];

      // --> REPLACE windowedOrdersRef with the new window's rows
      const nextMap = new Map();
      rows.forEach((order) => {
        const key = order.order_id || order.id;
        if (!key) return;
        const epochMs = order.created_at ? new Date(order.created_at).getTime() : Date.now();
        nextMap.set(key, { ...order, _epoch: epochMs });
      });
      windowedOrdersRef.current = nextMap;

      // Seed positionsRef (positions are not date-filtered server-side)
      //! Upsert through the registry — same full-identity keying as the initial-fetch seeding.
      const posRows = res?.data?.data?.positions || res?.data?.positions || [];
      posRows.forEach((pos) => {
        positionsRef.current.upsert(pos);
      });

      setWindowedOrdersVersion((v) => v + 1);
      console.log('[DIAG] OrdersPage — windowed replace done', { orders: rows.length });
    } catch (err) {
      console.log('[DIAG] OrdersPage — windowed fetch failed', err);
    }
    // Also fire runReconcile with the window: POST sweep backfills the range,
    // GET enriches with reconcile_state + broker data.
    // runReconcile writes back to ordersRef which then syncs into windowedOrdersRef
    // via the versions.orders/reconcile effect above.
    runReconcile({ force: false, from_dt: dateParams.from_dt, to_dt: dateParams.to_dt, account_id: acctId });
  }, [positionsRef, runReconcile]);

  const handleDateRangeChange = useCallback((range) => {
    setDateRange(range);
    console.log('[DIAG] OrdersPage — date range changed', range);
    _fetchAndReplaceWindow(range, accountId);
  }, [accountId, _fetchAndReplaceWindow]);

  const handleAccountChange = useCallback((acctId) => {
    setAccountId(acctId);
    console.log('[DIAG] OrdersPage — account filter changed', acctId);
    //! R102.11 — account-filter change must re-fetch the current window scoped to new account.
    _fetchAndReplaceWindow(dateRange, acctId);
  }, [dateRange, _fetchAndReplaceWindow]);

  //! C2 (T5, fix/bd-trades-no-pair-no-pnl) — awaits runReconcile's {ok, rateLimited,
  //! retry_after} contract (TradingDataContext.jsx), disables the button + swaps its
  //! label while in-flight, and surfaces a rate-limit message instead of silently
  //! looking like a success on a rapid repeat click (01-investigation.md C2).
  //! R102.11 — forward current accountId AND the current window on explicit reconcile.
  const handleReconcileNow = useCallback(async () => {
    if (reconcileInFlightRef.current) return; // guard: one in-flight reconcile at a time
    reconcileInFlightRef.current = true;
    const dateParams = _presetToDateRange(dateRange);
    console.log('[DIAG] OrdersPage — Reconcile now clicked', { ...dateParams, accountId });
    setReconcileInFlight(true);
    setReconcileRateLimit(null);
    try {
      const result = await runReconcile({
        force: true, from_dt: dateParams.from_dt, to_dt: dateParams.to_dt, account_id: accountId,
      });
      // --> Always clear any running countdown before starting a new one — a NEW 429 must
      //     not tick alongside a stale interval from a previous rate-limit.
      if (reconcileRateLimitIntervalRef.current) {
        clearInterval(reconcileRateLimitIntervalRef.current);
        reconcileRateLimitIntervalRef.current = null;
      }
      if (result?.rateLimited) {
        const initialRetryAfter = result.retry_after ?? 30;
        setReconcileRateLimit({ retryAfter: initialRetryAfter });
        reconcileRateLimitIntervalRef.current = setInterval(() => {
          setReconcileRateLimit((prev) => {
            if (!prev) return prev;
            const next = prev.retryAfter - 1;
            if (next <= 0) {
              clearInterval(reconcileRateLimitIntervalRef.current);
              reconcileRateLimitIntervalRef.current = null;
              return null;
            }
            return { retryAfter: next };
          });
        }, 1000);
      } else {
        setReconcileRateLimit(null);
      }
    } finally {
      reconcileInFlightRef.current = false;
      setReconcileInFlight(false);
    }
  }, [runReconcile, accountId, dateRange]);

  //! F2 — clear the countdown interval on unmount so it never ticks against an unmounted page.
  useEffect(() => {
    return () => {
      if (reconcileRateLimitIntervalRef.current) {
        clearInterval(reconcileRateLimitIntervalRef.current);
        reconcileRateLimitIntervalRef.current = null;
      }
    };
  }, []);

  const handleRowClick = useCallback((order) => {
    setDrawerOrder(order);
    setDrawerOpen(true);
    //--> #10474 — lazy, read-only fetch of this order's lifecycle timeline. Distinct from the
    //   R104.6 broker-snapshot contract (that stays snapshot-only, no fetch): this is a cheap OMS
    //   read that fills the drawer's variable broker-update / cancel / fill events. A rejection
    //   row never became an order, so it has no timeline — skip the fetch.
    const oid = order?.order_id ?? order?.orderId;
    setTimelineEvents(null);
    setTimelineError(false);
    timelineOidRef.current = oid ?? null;
    if (!oid || order?._isRejection) return;
    setTimelineLoading(true);
    OrdersService.getOrderTimeline(oid)
      .then((res) => {
        if (timelineOidRef.current !== oid) return; // stale: user moved to another row / closed
        setTimelineEvents(res?.data?.data?.events ?? []);
      })
      .catch((err) => {
        if (timelineOidRef.current !== oid) return;
        console.log('[DIAG] OrdersPage — timeline fetch failed', { oid, err });
        setTimelineEvents(null); // keep the column-spine fallback; never claim "no events"
        setTimelineError(true);
      })
      .finally(() => { if (timelineOidRef.current === oid) setTimelineLoading(false); });
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setDrawerOrder(null);
    timelineOidRef.current = null;
    setTimelineEvents(null);
    setTimelineError(false);
    setTimelineLoading(false);
  }, []);

  const handleRefreshBroker = useCallback(() => {
    console.log('[DIAG] OrdersPage — drawer Refresh broker data clicked');
    runReconcile({ force: true });
  }, [runReconcile]);

  // --> R5.3/R6.4 — REST re-fetch of a single order row (not the whole window) after a
  //   cancel/modify resolves, so the row's status / pending_corrective_type reflects reality
  //   without waiting on a WS update. Patches windowedOrdersRef in place (same merge shape
  //   as the WS delta-sync effect above) and bumps the version so the memo re-computes.
  const refreshOrderRow = useCallback(async (orderId) => {
    if (!orderId) return;
    try {
      const res = await OrdersService.getOrderById(orderId);
      const updated = res?.data?.data ?? res?.data ?? null;
      if (!updated) return;
      const key = updated.order_id ?? updated.id ?? orderId;
      const existing = windowedOrdersRef.current.get(key) ?? {};
      const epochMs = existing._epoch ?? (updated.created_at ? new Date(updated.created_at).getTime() : Date.now());
      windowedOrdersRef.current.set(key, { ...existing, ...updated, _epoch: epochMs });
      setWindowedOrdersVersion((v) => v + 1);
      console.log('[DIAG] OrdersPage — order row refreshed', { orderId, status: updated.status, pending_corrective_type: updated.pending_corrective_type });
    } catch (err) {
      console.log('[DIAG] OrdersPage — order row refresh failed', { orderId, err });
    }
  }, []);

  // --> R5.1/R5.2.1 — OrdersTable's CancelButton calls this and awaits the returned Promise
  //   (its disabled-while-pending state spans confirm-dialog-open through network resolution).
  //   Opens the confirm dialog; resolves only once the operator confirms (and the cancel +
  //   refresh complete) or dismisses (zero network requests fired).
  const handleCancelRequest = useCallback((orderId) => {
    return new Promise((resolve) => {
      setCancelConfirm({ orderId, resolve });
    });
  }, []);

  const handleCancelDismiss = useCallback(() => {
    setCancelConfirm((prev) => {
      prev?.resolve?.();
      return null;
    });
  }, []);

  const handleCancelConfirm = useCallback(async () => {
    const pending = cancelConfirm;
    setCancelConfirm(null);
    if (!pending) return;
    const { orderId, resolve } = pending;
    try {
      console.log('[DIAG] OrdersPage — operator confirmed cancel', { orderId });
      await OrdersService.cancelOrder(orderId);
      showSnackbar(`Cancel request for order ${orderId} submitted`, 'success');
    } catch (err) {
      console.log('[DIAG] OrdersPage — cancelOrder failed', { orderId, err });
      const msg = err?.response?.data?.error?.message || err?.message || 'Cancel request failed';
      showSnackbar(msg, 'error');
    } finally {
      await refreshOrderRow(orderId);
      resolve?.();
    }
  }, [cancelConfirm, refreshOrderRow, showSnackbar]);

  //* Mobile — direct cancel. The phone presenter (MobileOrders) owns its own
  //  confirm sheet, so it calls this once the operator confirms (no desktop
  //  ConfirmDialog roundtrip). Same cancelOrder + snackbar + row-refresh as
  //  handleCancelConfirm, but takes the order object straight from the swipe.
  const handleCancelOrderDirect = useCallback(async (order) => {
    const orderId = order?.order_id ?? order?.id;
    if (!orderId) return;
    try {
      await OrdersService.cancelOrder(orderId);
      showSnackbar(`Cancel request for order ${orderId} submitted`, 'success');
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Cancel request failed';
      showSnackbar(msg, 'error');
    } finally {
      await refreshOrderRow(orderId);
    }
  }, [refreshOrderRow, showSnackbar]);

  // --> R6.1 — Modify entry point (OrdersTable's Modify button)
  const handleOpenModify = useCallback((order) => {
    console.log('[DIAG] OrdersPage — opening Modify modal', { orderId: order?.order_id });
    setModifyOrder(order);
  }, []);

  const handleModifyClose = useCallback(() => {
    setModifyOrder(null);
  }, []);

  // --> R6.4 — after a confirmed modify resolves, refresh the row and close the modal
  const handleModifySuccess = useCallback((orderId) => {
    setModifyOrder(null);
    showSnackbar(`Modify request for order ${orderId} submitted`, 'success');
    refreshOrderRow(orderId);
  }, [refreshOrderRow, showSnackbar]);

  //* Row backing the open cancel-confirm dialog (for the confirm message) — read live from
  //  windowedOrdersRef, not stored in state, so it always reflects the latest known row.
  const cancelConfirmRow = cancelConfirm ? windowedOrdersRef.current.get(cancelConfirm.orderId) : null;

  //* Phone tier — same live data (props), phone-shaped presentation. Reuses the
  //  page's derived orders/trades/positions + the tab/range/cancel handlers, so
  //  no data or wiring is duplicated (container/presenter split).
  if (isPhone) {
    return (
      <MobileOrders
        orders={orders}
        trades={trades}
        positions={positions}
        activeTab={activeTab}
        onTab={setActiveTab}
        dateRange={dateRange}
        onDateRange={handleDateRangeChange}
        onCancel={handleCancelOrderDirect}
        onRefresh={() => _fetchAndReplaceWindow(dateRange, accountId)}
      />
    );
  }

  /*>>> RENDER <<<*/

  return (
    <div className="min-h-screen bg-bg-1 pt-0 px-4 md:px-6 pb-10">
      {/*>>> PAGE HEADER <<<*/}
      <PageHeader
        title="Orders"
        subtitle="Live order book, executed trades, and broker-reconciled positions."
        //* 104 Element R (D67/R12.5) — the Stuck Orders entry point lives HERE, on
        //* the Orders surface, not under Admin (D67: the user is the primary path).
        //! Plain <a> (not react-router's <Link>) — this file is rendered standalone
        //! in Orders.test.jsx with no <Router> ancestor; <Link> throws
        //! "useHref() may be used only in the context of a <Router>" there. A plain
        //! anchor still navigates correctly inside the app shell (full route load).
        actions={
          <Button as="a" href="/StuckOrders" variant="secondary" size="sm">
            Stuck Orders
          </Button>
        }
      />

      {/*>>> CONTROL BAR — flat DS Tier-2 row (bordered-bottom): tabs left,
          filters / reconcile / live right, on the 32px rail <<<*/}
      {/* R102.1 AC(b) — provider stays mounted across tab switches */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-4 gap-y-3 border-b border-[var(--divider)]">
        <Tabs
          className="-mb-px"
          value={activeTab}
          onChange={setActiveTab}
          tabs={TABS.map((tab) => ({ label: tab, value: tab, testId: `tab-${tab.toLowerCase()}` }))}
        />
        <div className="pb-2.5">
        <OrdersTopBar
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          accountId={accountId}
          accounts={accounts}
          onAccountChange={handleAccountChange}
          brokerFilter={brokerFilter}
          brokerOptions={brokerOptions}
          onBrokerChange={setBrokerFilter}
          reconcileAsOf={reconcileAsOf}
          reconcileError={reconcileError}
          onReconcileNow={handleReconcileNow}
          isConnected={isConnected}
          reconcileInFlight={reconcileInFlight}
          reconcileRateLimit={reconcileRateLimit}
        />
        </div>
      </div>

      {/*>>> STRUCTURED DEGRADED BANNERS (R104.14) — three independent banners <<<*/}
      {/*! Driven by structured flags from the consolidated response — NEVER by message.includes() */}
      {omsDegraded && <OmsDegradedBanner />}
      {omsPositionsDegraded && <OmsPositionsDegradedBanner />}
      {classificationDegraded && <ClassificationDegradedBanner />}
      {brokerDegraded && <BrokerDataStaleBanner asOf={brokerOrdersAsOf} />}

      {/*>>> PER-BROKER DEGRADED BANNERS (R106.7, NEW-I5) — error_code-driven <<<*/}
      {Array.isArray(brokerStatus) && brokerStatus.filter((e) => e.degraded).map((e) => (
        <BrokerDegradedBanner
          key={`${e.broker}-${e.account_id ?? 'all'}`}
          broker={e.broker}
          error={e.error}
          errorCode={e.error_code}
        />
      ))}

      {/*>>> TAB PANELS <<<*/}
      {/* Positions tab */}
      <div role="tabpanel" hidden={activeTab !== 'Positions'} data-testid="tabpanel-positions">
        {activeTab === 'Positions' && (
          <PositionsTable
            positions={positions}
            showStrategy
          />
        )}
      </div>

      {/* Trades tab */}
      <div role="tabpanel" hidden={activeTab !== 'Trades'} data-testid="tabpanel-trades">
        {activeTab === 'Trades' && (
          trades.length === 0 ? (
            <div className="flex items-center justify-center rounded-xl border border-[var(--divider)] bg-bg-2 py-12">
              <span className="text-sm text-fg-2">No trades yet</span>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {trades.map((trade, idx) => (
                <TradeCard
                  key={trade.id ?? trade.trade_id ?? trade.entry_order_id ?? `trade-${idx}`}
                  trade={trade}
                  // C4 P4: BD single-row groups render in degenerate mode with broker-direct label.
                  // BD FIFO-pairing (Phase 1): a bdPaired group (real reconstructed entry+exit
                  // round trip, or an anchored open remainder) is NOT degenerate — it renders
                  // through the normal entry/exit header like an ALGO trade (still carries the
                  // Broker-direct badge via TradeCard's own isBrokerDirect/source_tag check).
                  trade_set_type={trade.isBrokerDirect && !trade.bdPaired ? 'degenerate' : 'algo_bracket'}
                  source_tag={trade.source_tag ?? undefined}
                />
              ))}
            </div>
          )
        )}
      </div>

      {/* Orders tab */}
      {/* R102.5 AC(d) — WS duplicate messages for same order_id are idempotent via Map key;
          windowedOrdersRef.current.set(orderId, merged) is idempotent — duplicate WS messages
          overwrite with the same data → no duplicate rows */}
      <div role="tabpanel" hidden={activeTab !== 'Orders'} data-testid="tabpanel-orders">
        {activeTab === 'Orders' && (
          <>
            {/* 104 D9 — subtle degraded notice; never blocks the orders view itself */}
            {rejectionsDegraded && (
              <p className="mb-2 text-xs text-warning" data-testid="rejections-degraded-notice">
                Rejection history may be incomplete — OMS degraded.
              </p>
            )}
            <OrdersTable
              orders={displayedOrders}
              rejections={displayedRejections}
              showStrategy
              onCancel={handleCancelRequest}
              onModify={handleOpenModify}
              onRowClick={handleRowClick}
            />
          </>
        )}
      </div>

      {/* Holdings tab — 10494-13 change 26; lazily fetched on first activation */}
      <div role="tabpanel" hidden={activeTab !== 'Holdings'} data-testid="tabpanel-holdings">
        {activeTab === 'Holdings' && (
          holdingsError ? (
            <div className="flex items-center justify-center rounded-xl border border-[var(--divider)] bg-bg-2 py-12">
              <span className="text-sm text-loss">{holdingsError}</span>
            </div>
          ) : holdingsLoading ? (
            <div className="flex items-center justify-center rounded-xl border border-[var(--divider)] bg-bg-2 py-12">
              <span className="text-sm text-fg-2">Loading holdings…</span>
            </div>
          ) : (
            <HoldingsTable
              holdings={holdings}
              accounts={holdingsAccounts}
              asOf={holdingsAsOf}
            />
          )
        )}
      </div>

      {/*>>> ORDER DETAIL DRAWER <<<*/}
      {/* R104.6 — sourced entirely from page-level reconcile snapshot, no auto-fetch on open */}
      <OrderDetailDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        order={drawerOrder}
        pageReconcileSnapshot={reconcileSnapshotRef?.current}
        onRefreshBroker={handleRefreshBroker}
        timelineEvents={timelineEvents}
        timelineLoading={timelineLoading}
        timelineError={timelineError}
      />

      {/*>>> CANCEL CONFIRM DIALOG (R5.2.1) <<<*/}
      {/* Dismiss fires zero network requests; confirm proceeds to OrdersService.cancelOrder (R5.1) */}
      <ConfirmDialog
        open={!!cancelConfirm}
        title="Cancel this order?"
        message={
          cancelConfirm
            ? `Order ${cancelConfirm.orderId}${cancelConfirmRow?.symbol ? ` (${cancelConfirmRow.symbol})` : ''} will be cancelled at the broker. This cannot be undone.`
            : null
        }
        confirmLabel="Cancel order"
        cancelLabel="Keep order"
        danger
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelDismiss}
      />

      {/*>>> MODIFY ORDER MODAL (R6.1-R6.7) <<<*/}
      <ModifyOrderModal
        open={!!modifyOrder}
        order={modifyOrder}
        onClose={handleModifyClose}
        onSuccess={handleModifySuccess}
      />
    </div>
  );
}

export default OrdersPage;
